import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Download, LineChart as LineChartIcon, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { TrainingService } from '@/services/TrainingService';
import { TERMINAL_STATUSES, type JobStatus, type TrainingJob, type TrainingMetricPoint } from '@/types/training';
import JobStatusBadge from './JobStatusBadge';

const POLL_INTERVAL_MS = 30_000;

const CHART_COLORS = {
  loss: '#ef4444',
  evalLoss: '#fca5a5',
  learningRate: '#3b82f6',
  perplexity: '#a855f7',
  gradNorm: '#f97316',
  failed: '#ef4444',
  cancelled: '#f59e0b',
} as const;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: TrainingJob | null;
};

type ChartDatum = {
  seq: number;
  step: number | null;
  epoch: number | null;
  x: number;
  xLabel: string;
  loss: number | null;
  eval_loss: number | null;
  learning_rate: number | null;
  ppl: number | null;
  grad_norm: number | null;
};

type MetricChartCardProps = {
  title: string;
  data: ChartDatum[];
  lines: Array<{
    key: keyof Pick<ChartDatum, 'loss' | 'eval_loss' | 'learning_rate' | 'ppl' | 'grad_norm'>;
    label: string;
    color: string;
    dashed?: boolean;
  }>;
  terminalMarker?: { x: number; label: string; color: string } | null;
};

function shouldPoll(status: JobStatus) {
  return !TERMINAL_STATUSES.includes(status);
}

function mergeMetricSeries(current: TrainingMetricPoint[], incoming: TrainingMetricPoint[]) {
  if (incoming.length === 0) return current;
  const merged = new Map<number, TrainingMetricPoint>();
  for (const point of current) merged.set(point.seq, point);
  for (const point of incoming) merged.set(point.seq, point);
  return Array.from(merged.values()).sort((a, b) => a.seq - b.seq);
}

function formatMetricValue(value: number | null | undefined, digits: number) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toFixed(digits);
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'training-metrics';
}

function formatCsvCell(value: string | number | null | undefined) {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  return /[",\n\r]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
}

function formatRelativeTime(timestamp: Date | null, now: number) {
  if (!timestamp) return 'just now';
  const seconds = Math.max(0, Math.floor((now - timestamp.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function MetricsTooltip({ active, payload }: any) {

  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as ChartDatum | undefined;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-border/70 bg-background/95 px-3 py-2 text-xs shadow-xl backdrop-blur">
      <div className="space-y-0.5 border-b border-border/70 pb-2">
        <p className="font-medium text-foreground">{point.step !== null ? `Step ${point.step}` : point.xLabel}</p>
        <p className="text-muted-foreground">Epoch {formatMetricValue(point.epoch, 2)}</p>
      </div>
      <div className="space-y-1 pt-2">
        {payload.map((entry: any) => {
          const digits = entry.dataKey === 'ppl' ? 3 : 4;
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}
              </span>
              <span className="font-mono text-foreground">{formatMetricValue(entry.value, digits)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricChartCard({ title, data, lines, terminalMarker }: MetricChartCardProps) {
  const hasData = lines.some((line) => data.some((point) => point[line.key] !== null && point[line.key] !== undefined));

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border/70 bg-background/70 p-2.5">
      <div className="mb-1.5 flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {!hasData ? <span className="text-[11px] text-muted-foreground">No data yet</span> : null}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 10, left: -8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.18)" />
              <XAxis
                dataKey="x"

                tickLine={false}
                axisLine={false}
                minTickGap={32}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                tickFormatter={(value) => `${Math.round(value)}`}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={46}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                domain={['auto', 'auto']}
                tickFormatter={(value) => {
                  if (typeof value !== 'number') return '';
                  return Math.abs(value) >= 1000 ? value.toExponential(1) : value.toFixed(2);
                }}
              />
              <Tooltip content={<MetricsTooltip />} />
              {terminalMarker ? (
                <ReferenceLine
                  x={terminalMarker.x}
                  stroke={terminalMarker.color}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                  label={{
                    value: terminalMarker.label,
                    position: 'insideTopRight',
                    fill: terminalMarker.color,
                    fontSize: 11,
                  }}
                />
              ) : null}
              {lines.map((line) => (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  name={line.label}
                  stroke={line.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  strokeDasharray={line.dashed ? '6 4' : undefined}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 text-sm text-muted-foreground">
            No data yet
          </div>
        )}
      </div>
    </div>
  );
}

export default function MetricsDrawer({ open, onOpenChange, job }: Props) {
  const { toast } = useToast();
  const [jobState, setJobState] = useState<TrainingJob | null>(job);
  const [series, setSeries] = useState<TrainingMetricPoint[]>([]);
  const [totalSteps, setTotalSteps] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [now, setNow] = useState(Date.now());
  const [exporting, setExporting] = useState(false);
  const lastSeqRef = useRef(0);
  const etagRef = useRef<string | null>(null);
  const chartsRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<JobStatus | null>(job?.status ?? null);

  useEffect(() => {
    setJobState(job);
  }, [job]);

  useEffect(() => {
    statusRef.current = jobState?.status ?? null;
  }, [jobState]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    const jobId = job?.id;

    if (!open || !jobId) {
      setSeries([]);
      setTotalSteps(null);
      setError(null);
      setLoading(false);
      setLastSyncedAt(null);
      lastSeqRef.current = 0;
      etagRef.current = null;
      return;
    }

    let cancelled = false;
    let intervalId: number | null = null;
    let currentController: AbortController | null = null;

    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const tick = async (initial = false) => {
      currentController?.abort();
      currentController = new AbortController();

      try {
        if (initial) {
          setLoading(true);
          setError(null);
          setSeries([]);
          setTotalSteps(null);
          lastSeqRef.current = 0;
          etagRef.current = null;
        }

        const shouldRefreshJob = statusRef.current ? shouldPoll(statusRef.current) : true;
        const [metricsResult, refreshedJob] = await Promise.all([
          TrainingService.getMetrics(jobId, {

            afterSeq: initial || lastSeqRef.current === 0 ? undefined : lastSeqRef.current,
            etag: initial ? undefined : etagRef.current ?? undefined,
            signal: currentController.signal,
          }),
          shouldRefreshJob ? TrainingService.getJob(jobId) : Promise.resolve(job),

        ]);

        if (cancelled) return;

        setJobState(refreshedJob ?? job);
        statusRef.current = (refreshedJob ?? job).status;
        if (!shouldPoll((refreshedJob ?? job).status)) {
          stopPolling();
        }

        if (metricsResult.etag) {
          etagRef.current = metricsResult.etag;
        }

        setLastSyncedAt(new Date());

        if (metricsResult.status === 200 && metricsResult.data) {
          setTotalSteps(metricsResult.data.total_steps ?? null);
          lastSeqRef.current = metricsResult.data.last_seq;
          setSeries((current) => (
            initial
              ? metricsResult.data.series
              : mergeMetricSeries(current, metricsResult.data.series)
          ));
        }
      } catch (e: any) {
        if (cancelled || e?.name === 'AbortError') return;
        setError(String(e?.message || e));
      } finally {
        if (!cancelled && initial) {
          setLoading(false);
        }
      }
    };

    void tick(true);

    if (shouldPoll(job.status)) {
      intervalId = window.setInterval(() => {
        void tick(false);
      }, POLL_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      currentController?.abort();
      stopPolling();
    };
  }, [job?.id, open]);

  const chartData = useMemo<ChartDatum[]>(() => (
    series.map((point) => ({
      seq: point.seq,
      step: point.step,
      epoch: point.epoch,
      x: point.step ?? point.epoch ?? point.seq,
      xLabel: point.step !== null && point.step !== undefined
        ? `Step ${point.step}`
        : point.epoch !== null && point.epoch !== undefined
          ? `Epoch ${point.epoch.toFixed(2)}`
          : `Seq ${point.seq}`,
      loss: point.loss,
      eval_loss: point.eval_loss,
      learning_rate: point.learning_rate,
      ppl: point.ppl,
      grad_norm: point.grad_norm,
    }))
  ), [series]);

  const latestPoint = series.length > 0 ? series[series.length - 1] : null;
  const observedMaxEpoch = useMemo(() => (
    series.reduce((max, point) => Math.max(max, point.epoch ?? 0), 0)
  ), [series]);

  const percentComplete = useMemo(() => {
    if (latestPoint?.step !== null && latestPoint?.step !== undefined && totalSteps) {
      return Math.max(0, Math.min(100, Math.round((latestPoint.step / totalSteps) * 100)));
    }
    return Math.max(0, Math.min(100, Math.round((jobState?.progress ?? 0) * 100)));
  }, [jobState?.progress, latestPoint?.step, totalSteps]);

  const progressCaption = useMemo(() => {
    if (!jobState) return 'Loading training progress…';

    const stepLabel = latestPoint?.step !== null && latestPoint?.step !== undefined
      ? `Step ${latestPoint.step}${totalSteps ? ` / ${totalSteps}` : ''}`
      : totalSteps
        ? `Step — / ${totalSteps}`
        : 'Step —';

    const epochTarget = jobState.max_epochs || observedMaxEpoch || null;
    const epochLabel = latestPoint?.epoch !== null && latestPoint?.epoch !== undefined
      ? `Epoch ${latestPoint.epoch.toFixed(2)}${epochTarget ? ` / ${epochTarget}` : ''}`
      : epochTarget
        ? `Epoch — / ${epochTarget}`
        : 'Epoch —';

    return `${stepLabel} · ${epochLabel} · ${percentComplete}% complete`;
  }, [jobState, latestPoint, observedMaxEpoch, percentComplete, totalSteps]);

  const terminalMarker = useMemo(() => {
    if (!jobState || !latestPoint) return null;
    if (jobState.status === 'failed') {
      return {
        x: latestPoint.step ?? latestPoint.epoch ?? latestPoint.seq,
        label: 'Failed',
        color: CHART_COLORS.failed,
      };
    }
    if (jobState.status === 'cancelled') {
      return {
        x: latestPoint.step ?? latestPoint.epoch ?? latestPoint.seq,
        label: 'Cancelled',
        color: CHART_COLORS.cancelled,
      };
    }
    return null;
  }, [jobState, latestPoint]);

  const exportCharts = async () => {
    if (!chartsRef.current || !jobState) return;

    setExporting(true);
    try {
      const dataUrl = await toPng(chartsRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: getComputedStyle(chartsRef.current).backgroundColor || '#0b1020',
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${slugify(jobState.output_name)}-training-charts.png`;
      link.click();
    } catch (e: any) {
      toast({
        title: 'Could not export charts',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const exportData = () => {
    if (!jobState || chartData.length === 0) return;

    const headers = [
      'seq',
      'step',
      'epoch',
      'x',
      'x_label',
      'train_loss',
      'eval_loss',
      'learning_rate',
      'perplexity',
      'gradient_norm',
    ];
    const rows = chartData.map((point) => [
      point.seq,
      point.step,
      point.epoch,
      point.x,
      point.xLabel,
      point.loss,
      point.eval_loss,
      point.learning_rate,
      point.ppl,
      point.grad_norm,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(formatCsvCell).join(','))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugify(jobState.output_name)}-training-data.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const updatedLabel = formatRelativeTime(lastSyncedAt, now);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[92dvh] max-h-[calc(100dvh-1rem)] w-[98vw] max-w-[1400px] gap-0 overflow-hidden rounded-3xl border-border/70 bg-background/95 p-0 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/90">
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-border/70 bg-gradient-to-br from-canada-red/10 via-background to-background px-4 py-2 sm:px-6">
            <div className="flex flex-col gap-2 pr-10 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <LineChartIcon className="h-4 w-4 text-canada-red" />
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Training charts</p>
                </div>
                <DialogTitle className="mt-1.5 max-w-5xl truncate text-lg font-semibold leading-tight text-foreground lg:text-xl">
                  {jobState ? `${jobState.output_name} · ${jobState.base_model.split('/').pop()} · ${jobState.training_type.toUpperCase()}` : 'Training metrics'}
                </DialogTitle>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {jobState ? <JobStatusBadge status={jobState.status} /> : null}
                  <span className="rounded-full border border-border/70 bg-background/70 px-3 py-0.5 text-xs text-muted-foreground sm:text-sm">
                    {progressCaption}
                  </span>
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportData}
                  disabled={chartData.length === 0}
                  className="w-fit bg-background/80"
                >
                  <Download className="h-4 w-4" />
                  Export Data
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void exportCharts()}
                  disabled={exporting || chartData.length === 0}
                  className="w-fit bg-background/80"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export PNG
                </Button>
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2 sm:px-6">
            {jobState?.status === 'failed' ? (
              <div className="mb-2 shrink-0 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-1.5 text-sm text-destructive">
                Training failed at the latest recorded step. See the events log for the failure details.
              </div>
            ) : null}

            {jobState?.status === 'cancelled' ? (
              <div className="mb-2 shrink-0 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-sm text-amber-600">
                This run was cancelled. The charts are frozen at the last recorded metric point.
              </div>
            ) : null}

            {loading ? (
              <div className="flex h-full items-center justify-center rounded-3xl border border-border/70 bg-muted/20 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading training metrics…
              </div>
            ) : error ? (
              <div className="rounded-3xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-border/70 bg-muted/20 px-6 text-center">
                <LineChartIcon className="mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-lg font-medium text-foreground">Waiting for first training metrics…</p>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  The backend has not emitted chart points yet. Keep this panel open and it will update automatically.
                </p>
              </div>
            ) : (
              <div ref={chartsRef} className="grid min-h-0 flex-1 grid-rows-4 gap-2 rounded-3xl bg-background p-0.5 lg:grid-cols-2 lg:grid-rows-2">
                <MetricChartCard
                  title="Loss"
                  data={chartData}
                  terminalMarker={terminalMarker}
                  lines={[
                    { key: 'loss', label: 'Train loss', color: CHART_COLORS.loss },
                    { key: 'eval_loss', label: 'Eval loss', color: CHART_COLORS.evalLoss, dashed: true },
                  ]}
                />
                <MetricChartCard
                  title="Learning rate"
                  data={chartData}
                  terminalMarker={terminalMarker}
                  lines={[{ key: 'learning_rate', label: 'Learning rate', color: CHART_COLORS.learningRate }]}
                />
                <MetricChartCard
                  title="Perplexity"
                  data={chartData}
                  terminalMarker={terminalMarker}
                  lines={[{ key: 'ppl', label: 'Perplexity', color: CHART_COLORS.perplexity }]}
                />
                <MetricChartCard
                  title="Gradient norm"
                  data={chartData}
                  terminalMarker={terminalMarker}
                  lines={[{ key: 'grad_norm', label: 'Gradient norm', color: CHART_COLORS.gradNorm }]}
                />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-border/70 bg-background/80 px-4 py-1.5 sm:px-6">
            <div className="flex flex-col gap-0.5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <span>Last updated: {updatedLabel}</span>
              {latestPoint ? (
                <span>
                  Latest point: {latestPoint.step !== null && latestPoint.step !== undefined ? `step ${latestPoint.step}` : 'pre-step'} · epoch {formatMetricValue(latestPoint.epoch, 2)}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
